import { Fragment, useEffect, useState } from 'react';
import { AnnotatedStory } from './AnnotatedStory';
import { Clock } from './Clock';
import { ComicBoard } from './ComicBoard';
import { beamFont } from '../../shared/fonts';
import type { PublicState } from '../../shared/types';

/**
 * The audience stage (R-3): read-only, huge type, high contrast — legible
 * from the back row (N-4). Text streams in character by character exactly as
 * typed (B6), tinted per author (B5); jokers, penalties and time credits
 * appear as annotation notes in the left margin (2026-07-18).
 *
 * Extracted from ProjectionView on 2026-09-09 so the **playback** mode
 * (`#/play`) can put a recorded round on the very same stage: the live
 * projection feeds it the hub's broadcast, the replay feeds it a state
 * reconstructed from the export. Nothing in here knows the difference —
 * which is the point, because a replay that only *resembles* the show is
 * worth much less than the show itself, played again.
 *
 * Slides, in the order the evening runs through them (2026-09-08):
 *   idle   — the arcade-style "NAME VS NAME" card (2026-09-09); the logo and
 *            the wordmark already sit in the header, no need to repeat them
 *   intro  — the round's Schreibimpuls in title size, or the comic itself,
 *            so audience and authors know the task before the clocks start
 *   running/paused — the story stage, or the Bildaufgabe board
 *   ended  — the red ENDE screen, which clears itself after two seconds
 *            (2026-09-09) into the reading view: the annotated story, or the
 *            fully captioned comic for the discussion. Clicking the header
 *            brings the red screen back.
 */
/** How long the red ENDE screen holds before the reading view takes over. */
export const END_SCREEN_MS = 2_000;

export function ProjectionStage(props: {
  state: PublicState;
  /** Ticker pulse, for the countdowns between state updates. */
  now: number;
  /** serverNow − Date.now(); in playback, the offset into recorded time. */
  clockOffset: number;
  /** Displayed remaining ms for a seat (interpolated by the caller). */
  remainingMs: (seat: number) => number;
  /** Where the comic sheet's bytes come from — session route or local file. */
  comicSrc: (comicId: string) => string;
  /** Fired when the stage changes slide by itself (the filler sound cue). */
  onSlide?: () => void;
  /** Optional 🔊 unlock button for browsers that still block autoplay. */
  soundUnlock?: (() => void) | null;
  className?: string;
}) {
  const { state, now, clockOffset, remainingMs, comicSrc, onSlide, soundUnlock, className } = props;
  const [reading, setReading] = useState(false);
  const round = state.round;
  const phase = round.phase;
  const roundNumber = round.number;

  // The red ENDE screen is a beat, not a prompt: it holds for two seconds and
  // then hands the stage to the reading view on its own (2026-09-09), so the
  // moderation has nothing to click while the applause runs.
  useEffect(() => {
    if (phase !== 'ended') {
      setReading(false);
      return;
    }
    const id = setTimeout(() => {
      setReading(true);
      onSlide?.();
    }, END_SCREEN_MS);
    return () => clearTimeout(id);
    // `onSlide` is stable enough for one timeout; re-running on every sound
    // re-render would restart the beat.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, roundNumber]);

  const comic = round.comicId === null ? undefined : state.comics.find((c) => c.id === round.comicId);
  const showClocks = phase === 'running' || phase === 'paused';
  // Story typography, moderated live (2026-08-09): the vars land on .story
  // and .story--beam via CSS custom-property inheritance.
  const beamStyle = {
    '--story-font': beamFont(state.display.fontId).stack,
    '--story-scale': state.display.fontScale,
  } as React.CSSProperties;

  const roundLabel = round.kind === 'comic' ? `Bildrunde ${round.number}` : `Runde ${round.number}`;

  return (
    <div className={`beam ${className ?? ''}`} style={beamStyle}>
      <header
        className={`beam__head ${reading ? 'beam__head--reading' : ''}`}
        onClick={
          reading
            ? () => {
                setReading(false);
                onSlide?.();
              }
            : undefined
        }
        data-testid="beam-head"
      >
        {showClocks && (
          <Clock
            seat={state.authors[0]}
            seatIndex={0}
            round={round}
            remainingMs={remainingMs(0)}
            size="L"
            className="beam__clock beam__clock--left"
          />
        )}
        <div className="beam__center">
          <img className="beam__logo" src="/img/osiander-logo.png" alt="Osiander" />
          <div className="beam__brand">Schreibarena</div>
          <div className="beam__round" data-testid="beam-round">
            {roundLabel}
          </div>
          {round.prompt && phase !== 'intro' && <div className="beam__prompt">{round.prompt}</div>}
        </div>
        {showClocks && (
          <Clock
            seat={state.authors[1]}
            seatIndex={1}
            round={round}
            remainingMs={remainingMs(1)}
            size="L"
            className="beam__clock beam__clock--right"
          />
        )}
      </header>

      {phase === 'idle' && (
        <main className="beam__stage-wrap">
          <div className="beam__waiting">
            {/* The card before a round: who is fighting, arcade-style
                (2026-09-09). The logo and the wordmark are in the header
                already, and the PIN never goes on the beamer — joining runs
                over the QR code on the moderation screen. */}
            <div className="versus" data-testid="beam-intro">
              {Array.from({ length: state.maxAuthors }, (_, i) => {
                const seat = state.authors[i];
                return (
                  <Fragment key={i}>
                    {i > 0 && <span className="versus__vs">VS</span>}
                    <span
                      className={`versus__name ${seat ? '' : 'versus__name--open'}`}
                      style={seat ? ({ '--seat-color': seat.color } as React.CSSProperties) : undefined}
                      data-text={seat ? seat.name : `Autor:in ${i + 1}`}
                    >
                      {seat ? seat.name : `Autor:in ${i + 1}`}
                    </span>
                  </Fragment>
                );
              })}
            </div>
          </div>
        </main>
      )}

      {/* The task slide: everyone reads the assignment before the clocks run. */}
      {phase === 'intro' && (
        <main className="beam__stage-wrap">
          <div className="beam__waiting beam__waiting--task">
            {round.kind === 'comic' && comic ? (
              <div className="beam__comicintro" data-testid="beam-task">
                <ComicBoard
                  comic={comic}
                  src={comicSrc(comic.id)}
                  round={round}
                  authors={state.authors}
                  captions={false}
                  className="comicboard--intro"
                />
                <div className="beam__taskline">{round.prompt}</div>
              </div>
            ) : (
              <div className="beam__task" data-testid="beam-task">
                <div className="beam__task-kicker">Die Aufgabe</div>
                <div className="beam__task-text">{round.prompt || roundLabel}</div>
              </div>
            )}
            <div className="beam__seats">
              {state.authors.map((a) => (
                <span
                  key={a.index}
                  className={`beam__seat ${a.index === round.activeAuthor ? 'beam__seat--first' : ''}`}
                  style={{ borderColor: a.color }}
                >
                  {a.name}
                  {a.index === round.activeAuthor ? ' beginnt' : ''}
                </span>
              ))}
            </div>
          </div>
        </main>
      )}

      {phase !== 'idle' && phase !== 'intro' && (
        round.kind === 'comic' && comic ? (
          <main className="beam__stage-wrap beam__stage-wrap--comic comicfit">
            <ComicBoard
              comic={comic}
              src={comicSrc(comic.id)}
              round={round}
              authors={state.authors}
              showCaret={phase === 'running'}
              className="comicboard--beam"
            />
          </main>
        ) : (
          <AnnotatedStory
            round={round}
            authors={state.authors}
            now={now}
            clockOffset={clockOffset}
            showCaret={phase === 'running'}
            className="beam__stage"
          />
        )
      )}

      {phase === 'paused' && (
        <div className="beam__overlay" data-testid="beam-paused">
          <span>PAUSE</span>
        </div>
      )}
      {phase === 'ended' && !reading && (
        <div className="beam__overlay beam__overlay--end" data-testid="beam-ended">
          <span>ENDE</span>
          <small>
            {round.endReason === 'time' && 'Beide Uhren stehen auf 0:00.'}
            {round.endReason === 'completed' &&
              (round.kind === 'comic' ? 'Alle sechs Panels sind beschriftet.' : 'Gemeinsam für fertig erklärt.')}
            {round.endReason === 'forced' && 'Von der Moderation beendet.'}
          </small>
        </div>
      )}

      {soundUnlock && (
        <button className="beam__soundunlock" onClick={soundUnlock} data-testid="beam-sound-unlock">
          🔊 Ton aktivieren
        </button>
      )}
    </div>
  );
}
